/**
 * Contract-value range for an unsigned proposal — the lowest and highest
 * client-facing total across the selections the client can still make
 * (exclusive structure arrangements, optional add-ons, species, installation).
 * Used by surety bond requests so the letter can quote a range instead of a
 * single number before the client has signed. Pure; no firebase.
 */

import type { ProposalBuilderData, ProposalNodeSet } from '../types';
import { listExclusiveStructures } from './proposalExclusiveGroups';
import {
  computeProposalGrandTotals,
  getSignedCommercialListTotal,
  parseSpeciesSelectionsForPublicTotals,
  parseStructureSelectionsForPublicTotals,
} from './proposalPublicGrandTotals';

export type ContractRangeDimension = 'arrangement' | 'optional_structures' | 'species' | 'installation';

export interface ProposalContractRange {
  min: number;
  max: number;
  /** What the client can still choose that moves the price. */
  dimensions: ContractRangeDimension[];
  /** Scenarios evaluated. */
  scenarios: number;
}

export const CONTRACT_RANGE_DIMENSION_LABELS: Record<ContractRangeDimension, string> = {
  arrangement: 'structure arrangement',
  optional_structures: 'optional structures',
  species: 'species',
  installation: 'installation',
};

/** Independent optional add-ons (not members of the choose-one set). */
function listOptionalStructures(nodeSets: ProposalNodeSet[]): ProposalNodeSet[] {
  const exclusiveIds = new Set(listExclusiveStructures(nodeSets).map((ns) => ns.id));
  return nodeSets.filter((ns) => ns.enabled !== false && ns.clientOptional === true && !exclusiveIds.has(ns.id));
}

function speciesChoices(proposal: ProposalBuilderData): string[] {
  const offered = (proposal.offeredSpecies || []).map((s) => s.trim()).filter(Boolean);
  const fromOptions = new Set<string>();
  for (const ns of proposal.nodeSets || []) {
    for (const key of Object.keys(ns.billablePrices?.speciesOptions || {})) fromOptions.add(key);
  }
  const all = Array.from(new Set(offered.concat(Array.from(fromOptions))));
  return all.length > 1 ? all : [];
}

/**
 * Min/max total across client selections. Returns null when the proposal is
 * already signed (price is fixed) or when nothing is left for the client to
 * choose. Optional add-ons are evaluated all-on and all-off rather than every
 * subset — add-ons only ever add to the total once overhead is spread.
 */
export function computeProposalContractRange(proposal: ProposalBuilderData): ProposalContractRange | null {
  if (!proposal.nodeSets?.length) return null;
  if (getSignedCommercialListTotal(proposal) != null) return null;

  const nodeSets = proposal.nodeSets;
  const exclusive = listExclusiveStructures(nodeSets);
  const optional = listOptionalStructures(nodeSets);
  const species = speciesChoices(proposal);
  const installToggle = proposal.installOptional === true;

  const dimensions: ContractRangeDimension[] = [];
  if (exclusive.length > 1) dimensions.push('arrangement');
  if (optional.length > 0) dimensions.push('optional_structures');
  if (species.length > 0) dimensions.push('species');
  if (installToggle) dimensions.push('installation');
  if (dimensions.length === 0) return null;

  const baseSelections = parseStructureSelectionsForPublicTotals(proposal) || {};
  const { kitSelectedSpecies, selectedSpecies: baseSpecies } = parseSpeciesSelectionsForPublicTotals(proposal);

  const arrangementChoices: Array<string | null> = exclusive.length > 1 ? exclusive.map((ns) => ns.id) : [null];
  const optionalChoices: boolean[] = optional.length > 0 ? [true, false] : [true];
  const speciesList: Array<string | null> = species.length > 0 ? species : [null];
  const installChoices: boolean[] = installToggle ? [true, false] : [true];

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  let scenarios = 0;

  for (const arrangement of arrangementChoices) {
    for (const optionalsOn of optionalChoices) {
      for (const sp of speciesList) {
        for (const includeInstall of installChoices) {
          const structureSelections: Record<string, boolean> = { ...baseSelections };
          for (const ns of nodeSets) if (ns.enabled !== false) structureSelections[ns.id] = structureSelections[ns.id] !== false;
          if (arrangement) for (const ns of exclusive) structureSelections[ns.id] = ns.id === arrangement;
          for (const ns of optional) structureSelections[ns.id] = optionalsOn;

          const selectedSpecies: Record<string, string> = { ...baseSpecies };
          if (sp) for (const ns of nodeSets) selectedSpecies[ns.id] = sp;

          const totals = computeProposalGrandTotals(proposal, {
            includeInstall,
            selectedSpecies,
            kitSelectedSpecies: sp ?? kitSelectedSpecies,
            structureSelections,
          });
          const total = Math.round(totals.total);
          if (!Number.isFinite(total)) continue;
          scenarios += 1;
          if (total < min) min = total;
          if (total > max) max = total;
        }
      }
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max) || scenarios === 0) return null;
  if (min === max) return null;
  return { min, max, dimensions, scenarios };
}

export function describeContractRangeBasis(dimensions: ContractRangeDimension[]): string {
  const labels = dimensions.map((d) => CONTRACT_RANGE_DIMENSION_LABELS[d]);
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
